import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const StudentLibrary = ({ onNavigate, userInfo }) => {
    const [loading, setLoading] = useState(true);
    const [activeIssues, setActiveIssues] = useState([]);
    const [history, setHistory] = useState([]);
    const [fines, setFines] = useState({ total: 0, pending: 0, paid: 0 });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const loadData = async () => {
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');

                const response = await fetch(`${API_LINK}/api/student/library/dashboard`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const result = await response.json();

                if (result.success) {
                    setActiveIssues(result.data.activeIssues || []);
                    setHistory(result.data.history || []);
                    setFines(result.data.fines || { pending: 0, total: 0, paid: 0 });
                } else {
                    console.error('Failed to load library data:', result.message);
                }
            } catch (error) {
                console.error("Error fetching library data:", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Helper function for table cell
    const tableHeaderStyle = {
        padding: '1rem 1rem',
        textAlign: 'center',
        fontWeight: 700,
        color: '#ffffff',
        border: '2px solid #238548',
        fontSize: '0.95rem',
        backgroundColor: '#238548',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };

    const tableCellStyle = {
        padding: '1rem',
        border: '1px solid #c0c0c0',
        fontSize: '0.9rem',
        verticalAlign: 'middle',
        textAlign: 'center',
        backgroundColor: '#ffffff'
    };

    if (loading) {
        return (
            <Layout role="student" onNavigate={onNavigate} activePage="student-library" pageTitle="Library Record">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column' }}>
                    <div style={{ fontSize: '3rem', color: '#238548', animation: 'spin 1s linear infinite' }}>⏳</div>
                    <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#666' }}>Loading Library Record...</div>
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
            </Layout>
        );
    }

    return (
        <Layout role="student" onNavigate={onNavigate} activePage="student-library" pageTitle="Library Record">

            {/* Stats Cards Section */}
            <section style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: isMobile ? '0.75rem' : '1.5rem',
                marginBottom: isMobile ? '1rem' : '2rem'
            }}>
                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '2rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '3rem', color: '#238548', marginBottom: isMobile ? '0.3rem' : '1rem' }}>📖</div>
                    <h3 style={{ margin: isMobile ? '0 0 0.2rem 0' : '0 0 0.5rem 0', color: '#333', fontSize: isMobile ? '0.75rem' : '1.1rem' }}>Books Issued</h3>
                    <p style={{ fontSize: isMobile ? '1.3rem' : '2.2rem', fontWeight: 'bold', color: '#238548', margin: isMobile ? '0.2rem 0' : '0.5rem 0' }}>
                        {activeIssues.length}
                    </p>
                    <p style={{ fontSize: isMobile ? '0.65rem' : '1rem', color: '#666', margin: 0 }}>Currently Active</p>
                </div>

                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '2rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '3rem', color: '#f57c00', marginBottom: isMobile ? '0.3rem' : '1rem' }}>💰</div>
                    <h3 style={{ margin: isMobile ? '0 0 0.2rem 0' : '0 0 0.5rem 0', color: '#333', fontSize: isMobile ? '0.75rem' : '1.1rem' }}>Pending Fines</h3>
                    <p style={{ fontSize: isMobile ? '1.3rem' : '2.2rem', fontWeight: 'bold', color: '#f57c00', margin: isMobile ? '0.2rem 0' : '0.5rem 0' }}>
                        ₹{fines.pending}
                    </p>
                    <p style={{ fontSize: isMobile ? '0.65rem' : '1rem', color: '#666', margin: 0 }}>Action Required</p>
                </div>

                <div style={{
                    background: '#ffffff',
                    padding: isMobile ? '0.8rem 0.5rem' : '2rem',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    cursor: 'pointer'
                }}>
                    <div style={{ fontSize: isMobile ? '1.5rem' : '3rem', color: '#1976d2', marginBottom: isMobile ? '0.3rem' : '1rem' }}>📚</div>
                    <h3 style={{ margin: isMobile ? '0 0 0.2rem 0' : '0 0 0.5rem 0', color: '#333', fontSize: isMobile ? '0.75rem' : '1.1rem' }}>Total Read</h3>
                    <p style={{ fontSize: isMobile ? '1.3rem' : '2.2rem', fontWeight: 'bold', color: '#1976d2', margin: isMobile ? '0.2rem 0' : '0.5rem 0' }}>
                        {history.length + activeIssues.length}
                    </p>
                    <p style={{ fontSize: isMobile ? '0.65rem' : '1rem', color: '#666', margin: 0 }}>Books History</p>
                </div>
            </section>

            {/* Active Issues Section */}
            <section style={{ marginBottom: '2rem' }}>
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        background: '#238548',
                        color: '#ffffff',
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <span style={{ fontSize: '1.8rem' }}>📋</span>
                        <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Currently Issued Books</h3>
                    </div>
                    <div style={{ padding: '1.5rem' }}>
                        <div style={{
                            overflowX: 'auto',
                            borderRadius: '8px',
                            border: '2px solid #dee2e6',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                            {activeIssues.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff' }}>
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>Book Title</th>
                                            <th style={tableHeaderStyle}>Author</th>
                                            <th style={tableHeaderStyle}>Issue Date</th>
                                            <th style={tableHeaderStyle}>Due Date</th>
                                            <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeIssues.map((book, index) => (
                                            <tr key={book.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                                                <td style={tableCellStyle}><strong>{book.title}</strong></td>
                                                <td style={tableCellStyle}>{book.author}</td>
                                                <td style={tableCellStyle}>{book.issueDate}</td>
                                                <td style={{ ...tableCellStyle, color: '#d32f2f', fontWeight: 'bold' }}>{book.dueDate}</td>
                                                <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                    {book.warning ? (
                                                        <span style={{ backgroundColor: '#fff3e0', color: '#ef6c00', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>{book.warning}</span>
                                                    ) : (
                                                        <span style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>Active ({book.daysLeft} days)</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No active book issues found.</div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Recent History Section */}
            <section style={{ marginBottom: '2rem' }}>
                <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        background: '#238548',
                        color: '#ffffff',
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <span style={{ fontSize: '1.8rem' }}>🕒</span>
                        <h3 style={{ margin: '0, fontSize: 1.2rem' }}>Recent History & Fines</h3>
                    </div>
                    <div style={{ padding: '1.5rem' }}>
                        <div style={{
                            overflowX: 'auto',
                            borderRadius: '8px',
                            border: '2px solid #dee2e6',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                            {history.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff' }}>
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>Book Title</th>
                                            <th style={tableHeaderStyle}>Author</th>
                                            <th style={tableHeaderStyle}>Action Date</th>
                                            <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Fine</th>
                                            <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((item, index) => (
                                            <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                                                <td style={tableCellStyle}><strong>{item.title}</strong></td>
                                                <td style={tableCellStyle}>{item.author}</td>
                                                <td style={tableCellStyle}>{item.returnDate} (Returned)</td>
                                                <td style={{ ...tableCellStyle, textAlign: 'center', color: item.fine > 0 ? '#d32f2f' : '#333' }}>
                                                    {item.fine > 0 ? `₹${item.fine}` : '-'}
                                                </td>
                                                <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                                                    {item.fine > 0 ? (
                                                        <span style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>Returned Late</span>
                                                    ) : (
                                                        <span style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>Returned on Time</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No history available.</div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

        </Layout>
    );
};

export default StudentLibrary;
